/**
 * Eased layout-transition engine for the CanvasKit surfaces.
 *
 * The store recomputes exact node positions every frame (computeLayout). Drawn
 * verbatim, a reorder or resize-reflow *snaps* — sections jump to their new
 * slots. This animator sits between "target doc" and "draw": it keeps a map of
 * the currently-displayed rect per node and eases it toward the target each
 * frame, so sections glide the way Figma's auto-layout does.
 *
 * It is render-only and pure-ish: it holds its own displayed-rect state but
 * never mutates the input doc — it returns a shallow-cloned doc whose animated
 * nodes carry interpolated x/y/width/height (and a fade-in opacity for newly
 * added nodes).
 *
 * Node coordinates are parent-relative, so animating a container's x/y glides
 * its whole subtree; children whose relative position is unchanged settle
 * immediately and emit no patch.
 */
import type { SceneDocument, SceneNode } from '@/lib/scene-document';

/** Master switch — flip to false to fall back to instant snapping. */
export const ANIMATE_LAYOUT_TRANSITIONS = true;

/** Fields the animator needs to decide which nodes must track the cursor. */
interface FrozenInput {
  interactionMode: string;
  selectedIds: Set<string>;
  dragReorder: { draggedId: string } | null;
  doc: SceneDocument;
}

/**
 * Nodes that must track their target exactly this frame (no glide).
 *
 * Freeze the actively manipulated node AND its whole subtree: that way a free
 * resize's constraint-driven children track the handle 1:1 instead of lagging,
 * while non-descendant auto-layout siblings (which aren't frozen) glide into
 * their new slots. When idle, nothing is frozen so just-committed changes
 * settle smoothly to rest.
 */
export function computeFrozenIds(state: FrozenInput): Set<string> | undefined {
  if (state.interactionMode === 'idle') return undefined;

  const frozen = new Set<string>();
  const seeds = new Set<string>(state.selectedIds);
  if (state.dragReorder) seeds.add(state.dragReorder.draggedId);

  const stack = [...seeds];
  while (stack.length) {
    const id = stack.pop() as string;
    if (frozen.has(id)) continue;
    frozen.add(id);
    const node = state.doc.nodes[id];
    if (node?.children) stack.push(...node.children);
  }

  return frozen.size ? frozen : undefined;
}

// Exponential-smoothing time constant (ms). ~55ms ⇒ visually settles in
// ~150–180ms, matching Figma's auto-layout glide.
const TAU_MS = 55;
// Below this combined per-node delta (px) we consider a node at rest and snap
// it exactly to target so motion terminates cleanly (no infinite micro-easing).
const SETTLE_EPSILON = 0.4;
// A jump larger than this is treated as a teleport (snap, no glide) — guards
// against wild cross-canvas slides on document swaps or far frame moves.
const TELEPORT_PX = 2500;

interface DisplayRect {
  x: number;
  y: number;
  width: number;
  height: number;
  /** 0→1 entrance progress; <1 only for nodes that appeared mid-session. */
  enter: number;
}

export class LayoutAnimator {
  private displayed = new Map<string, DisplayRect>();
  private primed = false;
  private lastTime = 0;

  /** Forget all motion state (call when swapping to an unrelated document). */
  reset(): void {
    this.displayed.clear();
    this.primed = false;
    this.lastTime = 0;
  }

  /**
   * Ease displayed rects toward `target`'s node rects.
   *
   * @param frozenIds nodes that must track their target exactly this frame
   *   (the actively dragged/resized node — it follows the cursor 1:1).
   * @returns the doc to draw plus whether any node is still in motion (the
   *   caller keeps scheduling frames while `settling` is true).
   */
  apply(
    target: SceneDocument,
    now: number,
    frozenIds?: Set<string>,
  ): { doc: SceneDocument; settling: boolean } {
    // First call: seed displayed = target with no entrance fade so the canvas
    // doesn't flash in on load.
    if (!this.primed) {
      this.primed = true;
      this.lastTime = now;
      for (const [id, node] of Object.entries(target.nodes)) {
        this.displayed.set(id, rectOf(node, 1));
      }
      return { doc: target, settling: false };
    }

    const dt = this.lastTime ? Math.min(now - this.lastTime, 64) : 16;
    this.lastTime = now;
    const alpha = 1 - Math.exp(-dt / TAU_MS);

    let settling = false;
    const patches = new Map<string, Partial<SceneNode>>();
    const seen = new Set<string>();

    for (const id in target.nodes) {
      const node = target.nodes[id];
      seen.add(id);
      const tx = node.x;
      const ty = node.y;
      const tw = node.width;
      const th = node.height;

      let cur = this.displayed.get(id);

      // New node: snap into position and fade/scale in.
      if (!cur) {
        cur = rectOf(node, 0);
        this.displayed.set(id, cur);
      }

      const teleport =
        Math.abs(tx - cur.x) > TELEPORT_PX || Math.abs(ty - cur.y) > TELEPORT_PX;

      if (frozenIds?.has(id) || teleport) {
        cur.x = tx;
        cur.y = ty;
        cur.width = tw;
        cur.height = th;
        cur.enter = 1;
      } else {
        cur.x += (tx - cur.x) * alpha;
        cur.y += (ty - cur.y) * alpha;
        cur.width += (tw - cur.width) * alpha;
        cur.height += (th - cur.height) * alpha;
        cur.enter += (1 - cur.enter) * alpha;

        const delta =
          Math.abs(tx - cur.x) +
          Math.abs(ty - cur.y) +
          Math.abs(tw - cur.width) +
          Math.abs(th - cur.height) +
          (1 - cur.enter) * 8;

        if (delta > SETTLE_EPSILON) {
          settling = true;
        } else {
          cur.x = tx;
          cur.y = ty;
          cur.width = tw;
          cur.height = th;
          cur.enter = 1;
        }
      }

      // Only emit a patch when the displayed rect actually differs from target.
      if (
        cur.x !== tx ||
        cur.y !== ty ||
        cur.width !== tw ||
        cur.height !== th ||
        cur.enter < 1
      ) {
        const patch: Partial<SceneNode> = {
          x: cur.x,
          y: cur.y,
          width: cur.width,
          height: cur.height,
        };
        if (cur.enter < 1) {
          patch.opacity = (node.opacity ?? 1) * easeOut(cur.enter);
        }
        patches.set(id, patch);
      }
    }

    // Stop tracking nodes that no longer exist.
    if (this.displayed.size !== seen.size) {
      for (const id of this.displayed.keys()) {
        if (!seen.has(id)) this.displayed.delete(id);
      }
    }

    if (patches.size === 0) return { doc: target, settling };

    const nodes = { ...target.nodes };
    for (const [id, patch] of patches) {
      nodes[id] = { ...nodes[id], ...patch };
    }
    return { doc: { ...target, nodes }, settling };
  }
}

function rectOf(node: SceneNode, enter: number): DisplayRect {
  return { x: node.x, y: node.y, width: node.width, height: node.height, enter };
}

function easeOut(t: number): number {
  return 1 - (1 - t) * (1 - t);
}
